import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const fixedTermContractsApi = {
  /**
   * Get the fixed term contracts expiration report with filtering and pagination (ADR-090).
   * @param {Object} params - Query parameters
   * @param {string} [params.clusterId]
   * @param {string} [params.businessUnitId]
   * @param {string} [params.buScope] - 'direct' | 'hierarchy'
   * @param {boolean} [params.includeSubUnits]
   * @param {string} [params.employeeId]
   * @param {string} [params.representativeId] - ADR-092: filter to employees managed by this representative
   * @param {string} [params.reportingScope] - 'direct' | 'hierarchy' — span of the representative filter
   * @param {number} [params.expiresWithinDays] - 30 | 60 | 90
   * @param {boolean} [params.overdueOnly] - keep only overdue rows (distinct from the numeric windows)
   * @param {string} [params.orderDirection] - asc (soonest expiry first) or desc
   * @param {number} [params.limit]
   * @param {string} [params.cursor]
   * @returns {Promise<Object>} { summary, employees, pageInfo }
   */
  getFixedTermContractsReport: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.clusterId) {
        searchParams.set('clusterId', params.clusterId);
      }
      if (params.businessUnitId) {
        searchParams.set('businessUnitId', params.businessUnitId);
      }
      if (params.buScope) {
        searchParams.set('buScope', params.buScope);
      }
      if (params.includeSubUnits !== undefined) {
        searchParams.set('includeSubUnits', String(params.includeSubUnits));
      }
      if (params.employeeId) {
        searchParams.set('employeeId', params.employeeId);
      }
      if (params.representativeId) {
        searchParams.set('representativeId', params.representativeId);
        if (params.reportingScope) {
          searchParams.set('reportingScope', params.reportingScope);
        }
      }
      if (params.expiresWithinDays) {
        searchParams.set('expiresWithinDays', String(params.expiresWithinDays));
      }
      if (params.overdueOnly) {
        searchParams.set('overdueOnly', 'true');
      }
      if (params.orderDirection) {
        searchParams.set('orderDirection', params.orderDirection);
      }
      if (params.limit) {
        searchParams.set('limit', String(params.limit));
      }
      if (params.cursor) {
        searchParams.set('cursor', params.cursor);
      }

      const queryString = searchParams.toString();
      const url = `${API_BASE_URL}/reports/fixed-term-contracts${queryString ? `?${queryString}` : ''}`;

      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch fixed term contracts report', error.status || 500);
    }
  },

  /**
   * Dashboard-card summary, scoped server-side to the caller (ADR-091):
   * org-wide + per-cluster breakdown for admins/people partners, managed
   * hierarchy otherwise. Counts are cumulative (expiring60 includes
   * expiring30) — derive disjoint buckets by subtraction.
   * @returns {Promise<Object>} { totalContracts, overdue, expiring30, expiring60, expiring90, scope, breakdown? }
   */
  getFixedTermContractsSummary: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/reports/fixed-term-contracts/summary`);
    } catch (error) {
      throw new ApiError('Failed to fetch fixed term contracts summary', error.status || 500);
    }
  },
};
